import type { WidgetCatalogItem } from "@/features/control-tower/interfaces/widget-catalogue";
import type { WidgetConfigItem } from "@/features/control-tower/interfaces/update-widget";

export interface WidgetPayload {
  id: string;
  catalog_id: string;
  widgetType?: string;
  widgetCatalogType?: string;
  filters?: Record<string, any>;
  configs?: Array<Record<string, any>>;
  globalFilters?: Record<string, any>;
}

/**
 * Builds a widget payload object for API requests
 * @param widgetMeta - The widget metadata with widget_id and catalog_id
 * @param widgetCatalog - Array of widget catalog items to lookup catalog information
 * @param globalFilters - Optional global filters to include in the widget payload
 * @param localFilters - Optional local filters from the filter store
 * @param localConfigs - Optional local configs from the _config zone (merged with defaults)
 * @returns Widget payload object with id, catalog_id, and optional widgetType, widgetCatalogType, configs, and filters
 */
/**
 * Processes a config array and adds items to the appropriate collections
 */
const processConfigArray = (
  configs: Array<Record<string, any>>,
  configMap: Map<string, Record<string, any>>,
  untypedConfigs: Array<Record<string, any>>
): void => {
  for (const config of configs) {
    if (config.type) {
      configMap.set(config.type, config);
    } else {
      untypedConfigs.push(config);
    }
  }
};

/**
 * Resolves final widget configurations with fallback chain:
 * 1. localConfigs (from filter store) - user's session edits, highest priority
 * 2. savedConfigs (from widget meta/dashboard API) - persisted user preferences
 * 3. catalogItem.default_config - catalog defaults, lowest priority
 */
const resolveWidgetConfigs = (
  localConfigs: WidgetConfigItem[] | undefined,
  savedConfigs: WidgetConfigItem[] | undefined,
  catalogItem: WidgetCatalogItem | undefined
): Array<Record<string, any>> | undefined => {
  const defaultConfigs =
    catalogItem?.default_config && Array.isArray(catalogItem.default_config) ? catalogItem.default_config : [];

  // Use a Map keyed by 'type' to handle merging/overwriting for typed configs
  const configMap = new Map<string, Record<string, any>>();
  // Separate list for configs without 'type' property
  const untypedConfigs: Array<Record<string, any>> = [];

  // Process configs in priority order (lowest to highest)
  processConfigArray(defaultConfigs, configMap, untypedConfigs);

  if (savedConfigs?.length) {
    processConfigArray(savedConfigs, configMap, untypedConfigs);
  }

  if (localConfigs?.length) {
    processConfigArray(localConfigs, configMap, untypedConfigs);
  }

  // Combine typed and untyped configs
  const result = [...Array.from(configMap.values()), ...untypedConfigs];
  return result.length > 0 ? result : undefined;
};

/**
 * Resolves final widget filters with fallback chain:
 * 1. localFilters (from filter store) - user's session edits
 * 2. savedFilters (from widget meta/dashboard API) - persisted preferences
 * 3. catalogItem.filters defaults - catalog-defined default values (e.g. limit=5)
 * Returns array format: [{ key, values }] to match API expectations
 */
const resolveWidgetFilters = (
  localFilters: Array<{ key: string; values: any[] }> | undefined,
  savedFilters: Array<{ key: string; values: string[] }> | undefined,
  catalogItem: WidgetCatalogItem | undefined
): Array<{ key: string; values: any[] }> | undefined => {
  // Priority 1: localFilters from filter store (already in array format)
  const hasLocalFilters = localFilters && localFilters.length > 0;
  if (hasLocalFilters) {
    return localFilters;
  }

  // Priority 2: savedFilters from dashboard API (already in array format)
  const hasSavedFilters = savedFilters && savedFilters.length > 0;
  if (hasSavedFilters) {
    return savedFilters;
  }

  // Priority 3: catalog default filters (extract filters that have a 'default' value)
  if (catalogItem?.filters && Array.isArray(catalogItem.filters)) {
    const catalogFilters = catalogItem.filters as Array<{
      key?: string;
      name?: string;
      default?: string;
      [k: string]: any;
    }>;
    const defaultFilters = catalogFilters
      .filter((f) => f.default !== undefined && f.default !== null)
      .map((f) => ({
        key: f.key || f.name || "",
        values: [String(f.default)]
      }))
      .filter((f) => f.key.length > 0);

    if (defaultFilters.length > 0) {
      return defaultFilters;
    }
  }

  return undefined;
};

/**
 * Adds widget type information to payload if available
 */
const addWidgetTypeInfo = (payload: WidgetPayload, catalogItem: WidgetCatalogItem | undefined): void => {
  if (catalogItem?.appearance?.type) {
    payload.widgetType = catalogItem.appearance.type;
  }

  if (catalogItem?.widget_catalog_type) {
    payload.widgetCatalogType = catalogItem.widget_catalog_type;
  }
};

/**
 * Adds optional properties to widget payload if they have values
 */
const addOptionalProperties = (
  payload: WidgetPayload,
  configs: Array<Record<string, any>> | undefined,
  filters: Array<{ key: string; values: any[] }> | undefined
): void => {
  if (configs) {
    payload.configs = configs;
  }

  const hasFilters = filters && filters.length > 0;
  if (hasFilters) {
    payload.filters = filters;
  }
};

export const buildWidgetPayload = (
  widgetMeta: {
    widget_id: string;
    catalog_id: string;
    configs?: WidgetConfigItem[];
    filters?: Array<{ key: string; values: string[] }>;
  },
  widgetCatalog: WidgetCatalogItem[],
  localFilters?: Array<{ key: string; values: any[] }>,
  localConfigs?: WidgetConfigItem[]
): WidgetPayload => {
  const catalogItem = widgetCatalog.find((cat) => cat.catalog_id === widgetMeta.catalog_id);
  const widgetPayload: WidgetPayload = {
    id: widgetMeta.widget_id,
    catalog_id: widgetMeta.catalog_id
  };

  addWidgetTypeInfo(widgetPayload, catalogItem);

  // Use saved configs/filters from widget meta (dashboard API)
  const configs = resolveWidgetConfigs(localConfigs, widgetMeta.configs, catalogItem);
  const filters = resolveWidgetFilters(localFilters, widgetMeta.filters, catalogItem);

  addOptionalProperties(widgetPayload, configs, filters);

  return widgetPayload;
};
